import type { LibraryMode } from "@/lib/tier";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LibraryItemEditor } from "@/components/library/LibraryItemEditor";
import { LibraryCategoryPicker } from "@/components/library/LibraryCategoryPicker";
import { WorkspaceMembershipPicker } from "@/components/library/WorkspaceMembershipPicker";
import { libraryCopy } from "@/lib/library";
import type { LibraryCategory, LibraryCategoryColorKey } from "@/lib/libraryCategories";
import type { Workspace } from "@/hooks/useWorkspace";
import type { EditableDraft, LibraryRecord } from "./myLibraryHelpers";

type Props = {
  suffix: "mobile" | "desktop";
  draft: EditableDraft;
  onChange: (draft: EditableDraft) => void;
  editingItem: LibraryRecord | null;
  active: Workspace | null;
  workspaces: Workspace[];
  libraryMode: LibraryMode;
  saving: boolean;
  editorWorkspaceIds: string[];
  setEditorWorkspaceIds: (ids: string[]) => void;
  moveTargetWorkspaceId: string;
  setMoveTargetWorkspaceId: (id: string) => void;
  onMove: () => void;
  categories: LibraryCategory[];
  editorCategoryIds: string[];
  setEditorCategoryIds: (ids: string[]) => void;
  defaultCategory: LibraryCategory | null;
  isNeutralDefault: boolean;
  onCreateCategory: (values: { name: string; color: LibraryCategoryColorKey }) => Promise<LibraryCategory>;
  onDelete: () => void;
  onSave: () => void;
};

/** Renders the My Library item editor body — workspace visibility section,
 *  bibliographic editor, category picker, and the save/remove button row. */
export const MyLibraryEditorBody = ({
  suffix,
  draft,
  onChange,
  editingItem,
  active,
  workspaces,
  libraryMode,
  saving,
  editorWorkspaceIds,
  setEditorWorkspaceIds,
  moveTargetWorkspaceId,
  setMoveTargetWorkspaceId,
  onMove,
  categories,
  editorCategoryIds,
  setEditorCategoryIds,
  defaultCategory,
  isNeutralDefault,
  onCreateCategory,
  onDelete,
  onSave,
}: Props) => {
  const editorOwningWorkspaceId = editingItem?.workspace_id ?? active?.id ?? "";
  const showSharedPicker = libraryMode === "shared" && workspaces.length > 1;
  const showMoveControl = libraryMode === "isolated" && workspaces.length > 1 && !!editingItem;
  const moveTargets = workspaces.filter((w) => w.id !== editingItem?.workspace_id);

  const workspaceSection = (showSharedPicker || showMoveControl) ? (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
      <p className="text-sm font-medium text-foreground">Workspace visibility</p>
      {showSharedPicker ? (
        <>
          <WorkspaceMembershipPicker
            workspaces={workspaces}
            value={editorWorkspaceIds}
            onChange={setEditorWorkspaceIds}
            owningWorkspaceId={editorOwningWorkspaceId}
            disabled={saving}
          />
          <p className="text-xs text-muted-foreground">
            Source stays editable in its owning workspace and is visible everywhere it’s shared.
          </p>
        </>
      ) : null}
      {showMoveControl ? (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={moveTargetWorkspaceId} onValueChange={setMoveTargetWorkspaceId}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Move to workspace…" />
            </SelectTrigger>
            <SelectContent>
              {moveTargets.map((w) => (
                <SelectItem key={w.id} value={w.id}>{w.workspace_label || w.display_name || "Workspace"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={onMove}
            disabled={saving || !moveTargetWorkspaceId || moveTargetWorkspaceId === editingItem?.workspace_id}
          >
            Move source
          </Button>
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      <LibraryItemEditor
        draft={draft}
        onChange={onChange}
        idPrefix={`library-${suffix}`}
        workspaceSection={workspaceSection}
        libraryItemId={editingItem?.id ?? null}
        workspaceId={editorOwningWorkspaceId || null}
      />
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Categories</p>
        <LibraryCategoryPicker
          categories={categories}
          value={editorCategoryIds}
          onChange={setEditorCategoryIds}
          onCreateCategory={onCreateCategory}
          disabled={saving}
        />
        <p className="text-xs text-muted-foreground">
          {defaultCategory
            ? `Default category preselected: ${defaultCategory.name}${isNeutralDefault ? " (neutral baseline)" : ""}.`
            : "Pick one or more categories for this source."}
        </p>
      </div>
      <div className="flex flex-wrap justify-between gap-2 pt-2">
        <div>
          {editingItem ? (
            <Button variant="outline" onClick={onDelete} disabled={saving}>
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          ) : null}
        </div>
        <Button onClick={onSave} disabled={saving || !draft.title.trim()}>
          {saving ? "Saving…" : libraryCopy.saveAction}
        </Button>
      </div>
    </div>
  );
};
